import {createAction, createAsyncThunk} from '@reduxjs/toolkit';

import getEventsList from 'api/events/get-events-list';
import getEventTypeOptions from 'api/events/get-event-type-options';
import getEventVenues from 'api/events/get-event-venues';

import {
    EventListItem,
    EventTypeOption,
    EventVenue,
    GetEventListRequest,
    GetEventListResponseMeta,
    GetEventTypeOptionsRequest,
} from 'api/events/events.types';
import dayjs from 'utils/dayjs';

import {
    EventFilterOption,
    EventGroup,
} from 'pages/event-management/utils/event-management.types';
import {eventsAdapter} from 'pages/event-management/store/past-event-list-adapters';
import {PastEventsListState} from 'pages/event-management/store/past-events-list-slice';

const CANCELED = 'ERR_CANCELED';

interface PastEventListDataParams {
    facilityId: number;
    searchParams: GetEventListRequest;
    handleAbortSignal?: (signal: AbortSignal) => void;
    abortSignal?: AbortSignal;
}

interface PastEventListReturn {
    entities: EventGroup[];
    meta: GetEventListResponseMeta;
}

interface PastEventListThunkAPI {
    state: {
        events: {
            pastEvents: PastEventsListState;
        };
    };
    rejectValue: unknown;
    signal: AbortSignal;
}

/**
 * A function to format the event filter options.
 */
const formatEventFilterOptions = (
    data: (EventTypeOption | EventVenue)[]
): EventFilterOption[] => {
    const formattedData = data.map((item) => ({
        label: item.title,
        id: item.id ? item.id.toString() : '',
        value: item.title,
    }));

    return formattedData;
};

/**
 * A Redux thunk for fetching event filter options.
 */
export const getEventFilterOptions = createAsyncThunk(
    'events/pastEventsList/getEventFilterOptions',
    async (
        {facilityId, fromDate, toDate}: GetEventTypeOptionsRequest,
        {rejectWithValue}
    ) => {
        try {
            const [eventTypeResponse, venueResponse] = await Promise.all([
                getEventTypeOptions({
                    facilityId,
                    fromDate: dayjs(fromDate),
                    toDate: dayjs(toDate),
                }),
                getEventVenues(facilityId),
            ]);

            const formattedEventTypes =
                formatEventFilterOptions(eventTypeResponse);
            const formattedVenues = formatEventFilterOptions(venueResponse);

            return {
                eventTypeIds: formattedEventTypes,
                venueIds: formattedVenues,
            };
        } catch (error) {
            console.error('getEventFilterOptions error:', {error});

            return rejectWithValue(error);
        }
    }
);

/**
 * A function to format the past events list data.
 */
const formatPastEventsListData = ({
    events,
    existingEvents,
}: {
    events: EventListItem[];
    existingEvents?: EventGroup[];
}) => {
    // Create a new array from existingEvents to avoid modifying the original
    const initialAcc = existingEvents ? [...existingEvents] : [];

    const groupedEvents = events.reduce((acc, event) => {
        const date = dayjs(event.eventStarts).format('YYYY-MM-DD');
        const existingGroupIndex = acc.findIndex(
            (eventGroup) => eventGroup.startDate === date
        );

        if (existingGroupIndex !== -1) {
            // Check if the event already exists in the group
            const existingGroup = acc[existingGroupIndex];
            const isDuplicate = existingGroup.events.some(
                (existingEvent) => existingEvent.eventId === event.eventId
            );

            if (!isDuplicate) {
                // Create a new array with the existing events plus the new one
                // instead of modifying the existing array
                acc[existingGroupIndex] = {
                    ...existingGroup,
                    events: [...existingGroup.events, event],
                };
            }
        } else {
            acc.push({
                startDate: date,
                events: [event],
            });
        }

        return acc;
    }, initialAcc);

    const sortedEvents = groupedEvents.sort((a, b) => {
        return dayjs(b.startDate).diff(dayjs(a.startDate));
    });

    return sortedEvents;
};

/**
 * A Redux thunk to get the searched past event list data.
 *
 * Action Type: events/pastEventsList/getSearchedPastEventsListData
 *
 * API Service: {@link getEventsList}
 */
export const getSearchedPastEventsListData = createAsyncThunk<
    // Return type of the payload creator
    PastEventListReturn,
    // First argument to the payload creator
    PastEventListDataParams,
    // ThunkAPI type
    PastEventListThunkAPI
>(
    'events/pastEventsList/getSearchedPastEventsListData',
    async (
        {facilityId, searchParams, handleAbortSignal, abortSignal},
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await getEventsList(facilityId, searchParams, {
                signal: abortSignal,
            });

            const formatedEventListResults = formatPastEventsListData({
                events: response.data.results,
            });

            const responseData = {
                entities: formatedEventListResults,
                meta: response.meta,
            };

            return responseData;
        } catch (error) {
            // If the error is due to the request being canceled, we don't want to log it
            if (error.code === CANCELED) {
                return;
            }

            console.error('getSearchedPastEventsListData error:', {error});

            return rejectWithValue(error);
        }
    }
);

/**
 * A Redux thunk to get the paginated past event list data.
 *
 * Action Type: events/pastEventsList/getPaginatedPastEventListData
 *
 * API Service: {@link getEventsList}
 */
export const getPaginatedPastEventListData = createAsyncThunk<
    // Return type of the payload creator
    PastEventListReturn,
    // First argument to the payload creator
    PastEventListDataParams,
    // ThunkAPI type
    PastEventListThunkAPI
>(
    'events/pastEventsList/getPaginatedPastEventListData',
    async (
        {facilityId, searchParams, handleAbortSignal, abortSignal},
        {getState, rejectWithValue, signal}
    ) => {
        try {
            handleAbortSignal(signal);

            // Get the existing events from state
            const state = getState().events.pastEvents.events;
            const existingEvents = eventsAdapter
                .getSelectors()
                .selectAll(state);

            // Fetch the next page of events form the API
            const response = await getEventsList(facilityId, searchParams, {
                signal: abortSignal,
            });

            // Format the response data, sort it, and merge it with existing events
            const formatedEventListResults = formatPastEventsListData({
                events: response.data.results,
                existingEvents,
            });

            const responseData = {
                entities: formatedEventListResults,
                meta: response.meta,
            };

            return responseData;
        } catch (error) {
            // If the error is due to the request being canceled, we don't want to log it
            if (error.code === CANCELED) {
                return;
            }

            console.error('getPaginatedPastEventListData error:', {error});

            return rejectWithValue(error);
        }
    }
);

/**
 * An action to update the applied filters for the past events list.
 */
export const updateAppliedFilters = createAction(
    'events/pastEventsList/updateAppliedFilters',
    (filters: {
        eventTypeIds?: number[];
        venueIds?: number[];
        fromDate: dayjs.Dayjs;
        toDate: dayjs.Dayjs;
    }) => {
        return {
            payload: filters,
        };
    }
);

/**
 * An action to clear the applied filters for the past events list.
 */
export const clearAppliedFilters = createAction(
    'events/pastEventsList/clearAppliedFilters'
);

/**
 * An action to set the search text for the past events list.
 */
export const setEventSearchText = createAction(
    'events/pastEventsList/setSearchText',
    (searchText: string) => {
        return {
            payload: searchText,
        };
    }
);
